import axios from "axios";
import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Spinner, Tab, Tabs } from "react-bootstrap";
import { FaArrowLeft, FaEdit, FaPlus } from "react-icons/fa";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { getURLbyEndPointV2 } from "../../../../../store/api";
import { getAuthUserlocal } from "../../../../../store/services";
import EditScheduleCalender from "./EditScheduleCalender";

const EditB2bRegistration = ({ registrationData, onUpdate, onClose }) => {
    const [activeTab, setActiveTab] = useState("participants");
    const [participants, setParticipants] = useState([]);
    const [newParticipant, setNewParticipant] = useState({
        firstName: "",
        lastName: "",
        designation: "",
        contact: "",
    });
    const [showModal, setShowModal] = useState(false);
    const [editIndex, setEditIndex] = useState(null);
    const [validated, setValidated] = useState(false);
    const [formError, setFormError] = useState("");
    const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const userId = getAuthUserlocal()._id;

    useEffect(() => {
        if (registrationData) {
            setParticipants(registrationData.participants || []);
            setSelectedTimeSlots(registrationData.slots || []);
        }
    }, [registrationData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewParticipant((prev) => ({
            ...prev,
            [name]: value,
        }));
        setValidated(false);
        setFormError("");
    };

    const handlePhoneChange = (phone) => {
        setNewParticipant((prev) => ({
            ...prev,
            contact: phone,
        }));
        setValidated(false);
        setFormError("");
    };

    const validateParticipant = () => {
        const { firstName, lastName, designation, contact } = newParticipant;
        if (!firstName || firstName.length < 2 || firstName.length > 20)
            return "First name must be between 2 and 20 characters";
        if (!lastName || lastName.length < 2 || lastName.length > 20)
            return "Last name must be between 2 and 20 characters";
        if (!designation || designation.length < 2 || designation.length > 20)
            return "Designation must be between 2 and 20 characters";
        if (!contact || contact.length < 8 || contact.length > 15)
            return "Contact must be between 8 and 15 characters";
        return null;
    };

    const handleAddOrUpdateParticipant = async () => {
        setValidated(true);
        const validationError = validateParticipant();
        if (validationError) {
            setFormError(validationError);
            return;
        }

        if (participants.length >= 3 && editIndex === null) {
            setFormError("Maximum 3 participants allowed.");
            return;
        }

        try {
            setLoading(true);
            if (editIndex !== null) {
                // Update existing participant
                const updatedParticipants = [...participants];
                updatedParticipants[editIndex] = newParticipant;
                
                const response = await axios.put(getURLbyEndPointV2("updateB2bParticipant") + userId, {
                    participant: newParticipant,
                    index: editIndex,
                });

                if (response.data.success) {
                    setParticipants(response.data.data.participants);
                }
            } else {
                // Add new participant
                const response = await axios.post(getURLbyEndPointV2("updateB2bParticipants") + userId, {
                    participant: newParticipant,
                });

                if (response.data.success) {
                    setParticipants(response.data.data.participants);
                }
            }

            setNewParticipant({ firstName: "", lastName: "", designation: "", contact: "" });
            setFormError("");
            setValidated(false);
            setShowModal(false);
        } catch (err) {
            setFormError(err.response?.data?.message || "Error adding/updating participant");
        } finally {
            setLoading(false);
        }
    };

    const handleEditParticipant = (index) => {
        setEditIndex(index);
        setNewParticipant(participants[index]);
        setShowModal(true);
    };

    const openAddParticipantModal = () => {
        setEditIndex(null);
        setNewParticipant({ firstName: "", lastName: "", designation: "", contact: "" });
        setFormError("");
        setValidated(false);
        setShowModal(true);
    };

    const handleTimeSlotUpdate = async (updatedSlots) => {
        try {
            setLoading(true);
            
            // Check if these are new slots or updates to existing slots
            const newSlots = updatedSlots.filter(slot => !slot._id);
            const existingSlots = updatedSlots.filter(slot => slot._id);
            
            let response;
            
            // If there are new slots, add them
            if (newSlots.length > 0) {
                response = await axios.post(getURLbyEndPointV2("addB2bSlot") + userId, {
                    slots: newSlots,
                });
            }
            
            // If there are existing slots to update, update them
            if (existingSlots.length > 0) {
                response = await axios.post(getURLbyEndPointV2("updateB2bSlots") + userId, {
                    slots: existingSlots,
                });
            }
            
            if (response && response.data.success) {
                setSelectedTimeSlots(response.data.data.slots);
                alert('Slots updated successfully!');
            }
        } catch (err) {
            setError(err.response?.data?.message || "Error updating slots");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormError("");
        setValidated(false);
    };

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between">
                <h4>Edit B2B Registration</h4>
                <Button variant="primary" size="sm" onClick={onClose} className="mb-3">
                    <FaArrowLeft className="me-2" /> Back to Registration
                </Button>
            </div>
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
                <Tab eventKey="participants" title="Participants">
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-bold mb-0">Representatives Information</h5>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={openAddParticipantModal}
                                disabled={participants.length >= 3}
                            >
                                <FaPlus className="me-1" /> Add Representative
                            </Button>
                        </div>
                        <div className="text-xs text-danger fw-bold" style={{ fontSize: "0.9rem" }}>
                            <span className="text-danger">*</span> In this section, the Collaborator can enter or edit the details of the representatives who will attend the B2B virtual meeting. You can add information for up to three representatives.
                        </div>
                        <hr />

                        {error && <Alert variant="danger">{error}</Alert>}

                        <div className="table-responsive">
                            <table className="table table-bordered table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th>First Name</th>
                                        <th>Last Name</th>
                                        <th>Designation</th>
                                        <th>Contact</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {participants.length > 0 ? (
                                        participants.map((participant, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>{participant.firstName}</td>
                                                <td>{participant.lastName}</td>
                                                <td>{participant.designation}</td>
                                                <td>{participant.contact}</td>
                                                <td>
                                                    <Button
                                                        variant="warning"
                                                        size="sm"
                                                        onClick={() => handleEditParticipant(index)}
                                                    >
                                                        <FaEdit /> Edit
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center">No participants added yet</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {participants.length < 3 && (
                            <div className="text-end mt-2">
                                <small className="text-muted">
                                    {3 - participants.length} more representative{3 - participants.length !== 1 ? 's' : ''} can be added
                                </small>
                            </div>
                        )}
                    </div>
                </Tab>
                <Tab eventKey="slots" title="Time Slots">
                    <div className="mb-4">
                        <h5 className="fw-bold mb-3">Manage Time Slots</h5>
                        <div className="text-xs text-danger fw-bold mb-3" style={{ fontSize: "0.9rem" }}>
                            <span className="text-danger">*</span> You can add new slots or manage your existing slots here. Slots that are already Confirmed cannot be modified.
                        </div>
                        <EditScheduleCalender
                            formData={{ selectedTimeSlots }}
                            setFormData={(data) => handleTimeSlotUpdate(data.selectedTimeSlots)}
                            existingSlots={selectedTimeSlots}
                            isEditMode={true}
                            onSlotsUpdate={handleTimeSlotUpdate}
                        />
                    </div>
                </Tab>
            </Tabs>

            {/* Modal for Add/Edit Participant */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editIndex !== null ? "Edit Representative" : "Add Representative"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form noValidate validated={validated}>
                        {formError && <Alert variant="danger">{formError}</Alert>}
                        <Row className="mb-3">
                            <Form.Group as={Col} md="6" controlId="validationCustom01">
                                <Form.Label className="fs-6">First Name*</Form.Label>
                                <Form.Control
                                    required
                                    type="text"
                                    minLength={2}
                                    maxLength={20}
                                    onChange={handleInputChange}
                                    value={newParticipant.firstName || ""}
                                    name="firstName"
                                    placeholder="First Name"
                                />
                                <Form.Control.Feedback type="invalid">
                                    First name must be between 2 and 20 characters.
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group as={Col} md="6" controlId="validationCustom02">
                                <Form.Label className="fs-6">Last Name*</Form.Label>
                                <Form.Control
                                    required
                                    minLength={2}
                                    maxLength={20}
                                    type="text"
                                    onChange={handleInputChange}
                                    value={newParticipant.lastName || ""}
                                    name="lastName"
                                    placeholder="Last Name"
                                />
                                <Form.Control.Feedback type="invalid">
                                    Last name must be between 2 and 20 characters.
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Row>
                        <Row className="mb-3">
                            <Form.Group as={Col} md="6" controlId="validationCustom04">
                                <Form.Label className="fs-6">Designation*</Form.Label>
                                <Form.Control
                                    required
                                    minLength={2}
                                    maxLength={20}
                                    type="text"
                                    placeholder="Designation"
                                    onChange={handleInputChange}
                                    value={newParticipant.designation || ""}
                                    name="designation"
                                />
                                <Form.Control.Feedback type="invalid">
                                    Designation must be between 2 and 20 characters.
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group as={Col} md="6" controlId="validationCustom05">
                                <Form.Label className="fs-6">Contact Number*</Form.Label>
                                <PhoneInput
                                    country={"in"}
                                    containerClass="w-100"
                                    value={newParticipant.contact || ""}
                                    onChange={handlePhoneChange}
                                    inputClass="form-control w-100"
                                    placeholder="Enter phone number"
                                />
                                <Form.Control.Feedback type="invalid">
                                    Contact number must be between 8 and 15 characters.
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddOrUpdateParticipant}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            editIndex !== null ? "Update" : "Add"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default EditB2bRegistration;